/**
 * DecisionSectionTable — tabela JEDNEJ sekcji kartoteki kokpitu decyzji („Grupuj według: Kartoteka").
 *
 * Tabela ma jeden wiersz na REKORD kartoteki (np. fakturę zakupu), z kolumnami listy tej kartoteki:
 * pola „pokaż na liście" z katalogu pól + układ listy ustawiony przez administratora (tylko do
 * odczytu), ten sam zestaw co na stronie kartoteki (patrz `section-kits.tsx`). Na końcu przypięta
 * kolumna „Decyzja": przyciski dróg dla KAŻDEGO otwartego kroku rekordu (rekord bywa na dwóch
 * krokach naraz, np. gałęzie równoległe) + „Otwórz formularz". Klik wiersza i „Otwórz formularz"
 * działają jak klik wiersza zwykłej listy decyzji (`useNavigateToTarget`).
 *
 * Dane: `GET /processes/my-pending-decisions/section` — sortowanie, filtry nagłówka / wiersza
 * „Szukaj…" i stronicowanie po stronie serwera (jak na liście faktur). Panel zawsze prosi o KOMPLET
 * kolumn zestawu (także tych ukrytych domyślnie), więc przełączanie widoczności kolumn (menu kolumny
 * → „Wybór kolumn") nie robi nowego zapytania, a kolumna włączona z ukrycia ma dane. Wybór kolumn NIE
 * jest zapamiętywany (`persistConfig={false}`): układ listy należy do administratora, a użytkownik
 * zmienia tylko bieżący widok.
 *
 * Układ listy czyta `useSectionListLayout` (trasa bez bramki uprawnień — zwykły użytkownik nie ma
 * dostępu do `GET /system-modules/:slug/layouts`). Tabela renderuje się dopiero po rozstrzygnięciu
 * układu i katalogu pól, żeby kolumny nie migały; stan (sortowanie, filtry, strona) żyje w
 * komponencie wewnętrznym montowanym PO tym — dzięki temu `defaultSort` z układu staje się
 * początkowym sortem zapytania.
 *
 * Zaznaczanie i „Akceptuj zaznaczone" (zakres „Moje"): wiersz sekcji to REKORD, a zaznaczenie żyje w
 * zakładce jako id KROKÓW, więc zaznaczenie rekordu zaznacza wszystkie jego kroki, a rekord jest
 * zaznaczony, gdy zaznaczone są wszystkie (`selection.ts`). Sekcja zgłasza zakładce wczytane kroki
 * (do licznika, akcji masowej i wstrzymania auto-odświeżania). Pasek akcji jest jeden, w zakładce —
 * sekcja nie ma własnego. Zasady zdejmowania zaznaczenia (zawsze tylko kroków TEJ sekcji):
 *  - zmiana strony, filtra lub sortu (także filtra „Szukaj…", którego zapytanie zmienia się po
 *    opóźnieniu — zaznaczenie zrobione w tym oknie zdejmuje zmiana klucza zapytania) oraz zakresu,
 *  - KAŻDA zmiana klucza zapytania sekcji, także nie z ręki użytkownika: automatyczna korekta strony
 *    poza zakresem (po odświeżeniu rekordów ubyło) i zmiana zestawu kolumn (np. po doczytaniu katalogu
 *    pól) — zbiór wierszy pod zaznaczeniem jest wtedy inny (efekt klucza zapytania w
 *    `useSectionSelectionSync`),
 *  - zwinięcie sekcji: po ponownym rozwinięciu startuje od pierwszej strony, bez filtrów i sortowania,
 *    więc zaznaczenia sprzed zwinięcia nie wolno zachować (tak samo jak przy zmianie strony),
 *  - każda zmiana wczytanych danych: rekord zaznaczony tylko CZĘŚCIOWO (np. doszedł nowy krok
 *    czekający na użytkownika) traci zaznaczenie kroków, bo jego pole wyboru nie pokazałoby tego, co
 *    liczą licznik i akcja masowa; rekord zaznaczony w całości zostaje zaznaczony.
 * Sekcja tylko do odczytu („Zespół") niczego nie zgłasza zakładce i niczego nie zdejmuje.
 * „Zaznacz wszystko" przy rekordzie bez kroków → `nextSliceStepIds` (nagłówek nie utyka).
 *
 * Widok „Lista / Karty": tryb sterowany z zewnątrz (`viewMode`, wspólny ze zwykłą listą decyzji);
 * przełącznik stoi w zakładce, a tabela dostaje tylko wartość BEZ `onViewModeChange` — inaczej
 * zasiewałaby tryb z `defaultViewMode` układu administratora i nadpisywała wybór użytkownika przy
 * każdym rozwinięciu sekcji. Karty mają tytuł jak karta listy kartoteki (pierwsza kolumna).
 *
 * Zakres „Zespół" (`scope='team'`): sekcja TYLKO DO ODCZYTU — bez przycisków decyzji i bez
 * zaznaczania, kolumna „Akcje" ma tylko „Otwórz formularz" (jak zwykły Zespół). Brak uprawnienia
 * podglądu zespołu (403) pokazuje ten sam łagodny komunikat co lista, zamiast błędu: zapytanie nie
 * ponawia 403 (`retryUnlessForbidden`), a werdykt „403" jest zapamiętany dla klucza zapytania, więc
 * komunikat trwa także w trakcie ponownego pobrania (zapytanie bez danych zeruje wtedy swój błąd).
 * Tak samo odpowiedź 402 (moduł kartoteki wyłączony po otwarciu sekcji — `MODULE_NOT_ACTIVE`): bez
 * ponowień i bez odpytywania, a zamiast „Lista nie została wczytana" z „Spróbuj ponownie" stoi
 * komunikat „Moduł tej kartoteki jest nieaktywny." (ten sam tekst co w oknach decyzji).
 *
 * Sumy „Razem" (widok „Lista", kartoteki z kolumnami kwot): pod tabelą stoi wiersz „Razem" z sumą
 * każdej kolumny kwot (`kit.sumSlugs` w granicach kolumn, które sekcja realnie ma — katalog pól
 * tenanta bywa uboższy od zestawu) — po filtrach sekcji, ze WSZYSTKICH stron, osobno dla każdej
 * waluty (kilka walut = kilka pozycji w komórce, jedna pod drugą). Sumy liczy serwer: panel prosi o
 * nie (`sums=1`) WYŁĄCZNIE w widoku „Lista" i tylko dla sekcji z kolumnami kwot; w „Kartach" nie ma
 * ani stopki, ani prośby o sumy. Tabela pomija wiersz „Razem", gdy żadna widoczna kolumna nie ma
 * sumy (np. kolumny kwot ukryte z menu kolumn). Zapytanie nie zmienia klucza przy przełączeniu
 * widoku (żeby przejście „Lista" ↔ „Karty" nie wczytywało wierszy od nowa i nie zdejmowało
 * zaznaczenia), więc dane wczytane w „Kartach" nie mają sum — po przejściu do „Listy" sekcja
 * dociąga je jednym ponowieniem zapytania (`requestedSums`). Gdy sekcja ma zaznaczone wiersze, sum
 * NIE dociąga: ponowne pobranie
 * wierszy omijałoby wstrzymanie auto-odświeżania przy zaznaczeniu (stopka pojawi się po zdjęciu
 * zaznaczenia albo przy najbliższym zwykłym odświeżeniu). Sekcje bez kwot (kontrahenci, pisma,
 * dokumenty handlowe i wewnętrzne) nie mają sum.
 *
 * Filtr „Firma": lista firm to firmy REKORDÓW sekcji z serwera (`companyOptions=1`), a nie firmy
 * użytkownika — serwer liczy ją bez filtrów nagłówka, więc nie zawęża się przy filtrowaniu innych
 * kolumn (`company-options.ts`). Filtr wysyła `{ field: 'company_id', mode: 'values' }` z id firmy.
 * Panel prosi o listę rzadko: tylko w zapytaniu „bazowym" sekcji (pierwsza strona, bez filtrów
 * nagłówka, w początkowym sortowaniu) oraz dopóki nie zna listy dla bieżącego zakresu („Moje" /
 * „Przeterminowane" / „Zespół"); przy innych stronach, filtrach i sortach używa listy
 * zapamiętanej dla zakresu. Listy są pamiętane OSOBNO dla każdego zakresu, więc powrót do zakresu
 * pokazuje jego firmy, także gdy odpowiedź z pamięci podręcznej listy nie niosła; dopóki zakres
 * nie ma własnej listy, filtr pokazuje ostatnio zapamiętaną (nie znika), a odpowiedź bez listy
 * dla takiego zakresu dociąga jedno ponowienie zapytania.
 *
 * Mechanizmy wydzielone do hooków obok (kolejność ich wywołań w `DecisionSectionList` jest
 * częścią kontraktu — komentarze w hookach): `useSectionCompanyOptions` (filtr „Firma"),
 * `useSectionSumsRefetch` (sumy „Razem" po powrocie z „Kart") i `useSectionSelectionSync`
 * (zaznaczenie ↔ zakładka).
 *
 * Zestawy STAŁE (dokumenty Symfonii Handel, `kit.source === 'static'`): kolumny są zapisane w kodzie,
 * więc sekcja NIE woła katalogu pól, układu administratora, listy użytkowników ani podpowiedzi wartości
 * filtra (`section/distinct`), a zapytanie nie niesie `columns`. Poza tym działa tak samo jak sekcje
 * z katalogu: zaznaczanie, „Akceptuj zaznaczone", przyciski decyzji, „Zespół" tylko do odczytu, karty.
 *
 * Zestawy KARTOTEK WŁASNYCH (`kit.source === 'custom'`, klucz grupy `custom.<slug>`): katalog
 * pól, układ listy i kategorie przychodzą w odpowiedzi samej sekcji (`columns`), bo decydujący
 * może nie mieć prawa do kartoteki — sekcja nie woła ani katalogu pól, ani układu, ani
 * `?columns=`. Do czasu pierwszej odpowiedzi nie ma kolumn, więc tabela pokazuje „Ładowanie", a
 * błąd pierwszego wczytania — „Lista nie została wczytana" ze „Spróbuj ponownie" (odmowy 402 i
 * 403 jak wyżej). Kolumny i wiersz filtrów → `custom-section-kit.tsx`; opcje list wyboru
 * (użytkownicy, relacje liczone z rekordów SAMEJ sekcji, słowniki) wczytuje
 * `useModuleListFilterOptions` w opakowaniu `CustomSectionTable` (tylko ten zestaw woła hak),
 * gdy katalog jest już znany. Katalog bez list pól i kategorii (np. `columns: null`) to jawny
 * błąd sekcji, a nie wyjątek. Sumy „Razem" proszą o `sums=1` od
 * razu w widoku „Lista" (pól „Kwota (PLN)" nie znamy przed pierwszą odpowiedzią; kartoteka bez
 * nich dostaje `{}`) i są formatowane jak komórki tego pola. Lista kartoteki własnej nie ma
 * kolumny „Firma", więc sekcja nie ma filtra „Firma" ani nie prosi o `companyOptions`. Układ
 * administratora nie ustawia sortu początkowego — jak na liście kartoteki, bez kliknięcia w
 * nagłówek serwer sortuje po pozycji i dacie utworzenia.
 */

import {
  createContext,
  type ReactElement,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { emptyListConfig, type ViewMode } from '@saas/shared';
import { useQuery } from '@tanstack/react-query';
import type { TFunction } from 'i18next';

import { Button } from '@/components/Button';
import {
  buildSectionDistinctTableId,
  type ColumnDefinition,
  type ColumnFilters,
  type ColumnHeaderFilters,
  ConfigurableTable,
  type TableLayoutConfig,
  TableLoading,
} from '@/components/configurable-table';
import { buildEndpointUrl } from '@/components/configurable-table/useDistinctValues';
import { useServerListControls } from '@/components/configurable-table/useServerListControls';
import type { ModuleField } from '@/components/fields/FieldRegistry';
import { DecisionButtons, type DecisionTarget } from '@/components/processes/DecisionButtons';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import {
  buildModuleListHeaderFilters,
  EMPTY_MODULE_LIST_FILTER_OPTIONS,
  type ModuleListFilterOptions,
} from '@/lib/custom-modules/module-list-columns';
import { useModuleListFilterOptions } from '@/lib/custom-modules/use-module-list-filter-options';
import {
  computeVisibleColumnParams,
  type ExtRefLabels,
  FieldCatalogErrorState,
  type SystemModuleFieldDef,
  useExtRefLabelsGetter,
  useSystemModuleFields,
} from '@/lib/ext-list-columns';
import { useListFilterOptions } from '@/lib/list-filter-options';
import {
  type CurrencySum,
  type CustomSectionColumns,
  type DecisionSectionResponse,
  type DecisionSectionSums,
  fetchDecisionSection,
  isModuleInactiveSectionError,
  isUnknownSectionError,
  type MyPendingDecisionRow,
  type PendingDecisionScope,
  TARGET_MODULE_INACTIVE_I18N_KEY,
} from '@/lib/processes/approvals-api';
import { formatMoney } from '@/lib/processes/format-money';
import { useNavigateToTarget } from '@/lib/processes/use-navigate-to-target';
import { isForbiddenError, retryUnlessForbidden } from '@/lib/query';
import { companyIdParam, useActiveCompanyScope } from '@/lib/use-active-company';
import { cn } from '@/lib/utils';

import { PaginationControls } from '../PaginationControls';

import {
  buildCustomSectionColumns,
  customSectionListConfig,
  customSectionListFields,
  isCustomSectionCatalog,
  stabilizeCustomSectionCatalog,
} from './custom-section-kit';
import {
  type CustomSectionKit,
  type DecisionSectionKit,
  type DecisionSectionRow,
  NO_SORT_SLUG_OVERRIDES,
  NO_SUM_SLUGS,
  type SectionKit,
  type StaticSectionKit,
} from './section-kits';
import {
  type DecisionSectionSelection,
  nextSliceStepIds,
  selectedRecordIds,
  stepIdsOfRows,
} from './selection';
import {
  useSectionCompanyOptions,
  useSectionCompanyOptionsFromQuery,
} from './useSectionCompanyOptions';
import { useSectionListLayout } from './useSectionListLayout';
import { useSectionSelectionSync } from './useSectionSelectionSync';
import { useSectionSumsRefetch } from './useSectionSumsRefetch';

/** Rozmiar strony tabeli sekcji (rekordów). */
const SECTION_PAGE_LIMIT = 50;

/** Opóźnienie wiersza „Szukaj…" pod nagłówkami (jak na liście faktur); eksport dla testów. */
export const COLUMN_FILTER_DEBOUNCE_MS = 300;

/** Brak zaznaczonych kroków (sekcja bez udziału w zaznaczeniu). */
const NO_MARKED_STEPS: ReadonlySet<string> = new Set<string>();

/** Brak pól kartoteki własnej (zestaw bez katalogu w odpowiedzi albo katalog niewczytany). */
const NO_MODULE_FIELDS: readonly ModuleField[] = [];

/**
 * Lista slugów z nowej odpowiedzi albo — gdy ma tę samą treść i kolejność — poprzednia (ta sama
 * tożsamość): lista `meta` jest zależnością memoizacji kolumn, a każda odpowiedź niesie jej kopię.
 */
function keepEqualSlugList(previous: string[] | undefined, next: string[]): string[] {
  const same =
    previous !== undefined &&
    previous.length === next.length &&
    previous.every((slug, index) => slug === next[index]);
  return same ? previous : next;
}

export interface DecisionSectionTableProps {
  /** Klucz grupy z nagłówka koszyka, np. `documents:purchase_invoice` albo `handel:sale`. */
  groupKey: string;
  /**
   * Zestaw sekcji: kartoteka z katalogu pól, stały zestaw dokumentów Handlu albo kartoteka własna
   * (jej katalog pól, układ i kategorie przychodzą w odpowiedzi sekcji, nie z osobnych zapytań).
   */
  kit: SectionKit;
  /** Zakres „Przeterminowane" (`?overdue=true`). */
  overdue: boolean;
  /** Zakres: `team` = decyzje podwładnych, sekcja tylko do odczytu (`?scope=team`). */
  scope: PendingDecisionScope;
  /**
   * Interwał auto-odświeżania w ms albo `false` (przełącznik wyłączony / okno decyzji otwarte —
   * decyduje rodzic, tak samo jak dla wspólnej listy).
   */
  refetchInterval: number | false;
  /**
   * Otwiera okno „Akceptacja" dla kroku z kolumny „Decyzja". Cel (`DecisionTarget`): droga
   * (`{ kind: 'route', routeKey }`, `routeKey` `null` = „Akceptacja", czyli droga domyślna) albo
   * pozycja „Cofnij do poprzedniego etapu" z menu „…" (`{ kind: 'stepBack' }`). Okno renderuje
   * rodzic — to samo co dla wspólnej listy decyzji.
   */
  onOpenDecision: (row: MyPendingDecisionRow, target: DecisionTarget) => void;
  /** Wspólny tryb widoku „Lista / Karty" (wybór trzyma zakładka, patrz opis pliku). */
  viewMode: ViewMode;
  /** Udział we wspólnym zaznaczeniu; brak = sekcja bez zaznaczania (patrz `selection.ts`). */
  selection?: DecisionSectionSelection;
}

/**
 * Nazwa dostępna przycisku „Otwórz formularz" w wierszu sekcji: „Otwórz formularz — sprawa #N".
 * Wiersz sekcji to REKORD, a nie sprawa — rekord ma zwykle jedną sprawę (jej numer), ale może
 * mieć kilka otwartych kroków tej samej sprawy (jeden numer) albo kilka spraw: wtedy nazwa ma
 * osobne sformułowanie w liczbie mnogiej z wszystkimi unikalnymi numerami rosnąco („sprawy #12,
 * #13"). Rekord bez kroków (nie powinno się zdarzyć) nie dostaje `aria-label` — zostaje widoczny
 * napis przycisku.
 */
function openFormAriaLabel(
  t: TFunction,
  decisions: readonly MyPendingDecisionRow[],
): string | undefined {
  const numbers = [...new Set(decisions.map((decision) => decision.instanceNumber))].sort(
    (a, b) => a - b,
  );
  if (numbers.length === 0) return undefined;
  return numbers.length === 1
    ? t('tenant.processes.approvals.openFormAria', { number: numbers[0] })
    : t('tenant.processes.approvals.openFormAriaMany', {
        numbers: numbers.map((number) => `#${number}`).join(', '),
      });
}

function SectionLoadError({
  groupKey,
  onRetry,
  retrying,
}: {
  groupKey: string;
  onRetry: () => void;
  /**
   * Trwa ponowne pobranie przy WIDOCZNYM błędzie — przycisk „Spróbuj ponownie" pokazuje ładowanie
   * i nie klika się dwa razy. Podaje je tylko wołający, którego stan błędu przeżywa ponowienie
   * (zapytanie o wiersze sekcji, patrz `loadFailed`); zapytanie o układ listy wraca na czas
   * ponowienia do „Ładowania".
   */
  retrying?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div
      className="flex flex-wrap items-center gap-3 text-sm text-error"
      role="alert"
      data-testid={`approvals-section-error-${groupKey}`}
    >
      <span>{t('tenant.processes.approvals.sections.loadError')}</span>
      <Button variant="secondary" size="sm" loading={retrying} onClick={onRetry}>
        {t('common.retry')}
      </Button>
    </div>
  );
}

/**
 * Zgłoszenie stanu „sekcja się wczytuje" do STAŁEGO regionu `role="status"` sekcji
 * (`SectionStatusRegion`). Region musi istnieć w DOM, zanim zmieni się jego treść — część czytników
 * ekranu nie zapowiada regionu zamontowanego razem z tekstem — więc stan ładowania nie niesie
 * własnego `role="status"`, tylko podmienia tekst w regionie dispatchera.
 */
const SectionLoadingReporterContext = createContext<((loading: boolean) => void) | null>(null);

/**
 * Stały region `role="status"` (wizualnie ukryty) w kontenerze rozwiniętej sekcji: pusty, a na czas
 * wczytywania sekcji (patrz `SectionLoading`) zawiera tekst „Ładowanie…". Liczy zgłoszenia (nie flagę),
 * żeby nakładanie się dwóch stanów ładowania w jednej klatce nie zgasiło komunikatu za wcześnie.
 */
function SectionStatusRegion({
  groupKey,
  children,
}: {
  groupKey: string;
  children: ReactNode;
}): ReactElement {
  const { t } = useTranslation();
  const [loadingCount, setLoadingCount] = useState(0);
  const report = useCallback((loading: boolean) => {
    setLoadingCount((count) => Math.max(0, count + (loading ? 1 : -1)));
  }, []);
  return (
    <SectionLoadingReporterContext.Provider value={report}>
      <div
        role="status"
        aria-live="polite"
        className="sr-only"
        data-testid={`approvals-section-status-${groupKey}`}
      >
        {loadingCount > 0 ? t('ui.configurableTable.loading') : null}
      </div>
      {children}
    </SectionLoadingReporterContext.Provider>
  );
}

/**
 * Stan ładowania sekcji przed pierwszą odpowiedzią (katalog pól / układ / katalog kartoteki
 * własnej). Zapowiedź dla czytnika ekranu idzie przez stały region `role="status"` sekcji
 * (`SectionStatusRegion`); sama animacja jest dla czytnika ukryta, żeby tekst nie padał dwa razy.
 */
function SectionLoading({ groupKey }: { groupKey: string }) {
  const report = useContext(SectionLoadingReporterContext);
  useEffect(() => {
    if (report === null) return undefined;
    report(true);
    return () => report(false);
  }, [report]);
  return (
    <div aria-hidden="true" data-testid={`approvals-section-loading-${groupKey}`}>
      <TableLoading inline />
    </div>
  );
}

/** Wejście budowy kolumn listy sekcji — to, co zależy od stanu komponentu (tłumaczenia, meta serwera, firmy). */
interface ListColumnsContext {
  t: TFunction;
  /** Stabilny getter etykiet pól-odwołań (tylko zestawy z katalogiem pól). */
  getRefLabels: () => ExtRefLabels | undefined;
  /** Firmy rekordów sekcji (z serwera, plus wybrane w filtrze) — opcje filtra „Firma". */
  companies: ReadonlyArray<{ id: string; name: string }>;
  /** Czy kolumna jest sortowalna serwerowo (`meta.sortable`; pyta po id kolumny, z remapem sortu). */
  isSortable: (columnId: string) => boolean;
  /** Czy kolumna jest filtrowalna serwerowo (`meta.filterable`; pyta po slugu backendu). */
  isFilterable: (slug: string) => boolean;
  /** Tekst zamiast surowego id dla odwołania, którego serwer nie rozwiązał. */
  unresolvedRefLabel: string;
  /**
   * Katalog kartoteki własnej z ostatniej odpowiedzi sekcji (tylko zestawy z katalogiem w
   * odpowiedzi); `undefined` = jeszcze nie przyszedł albo zestaw go nie używa.
   */
  responseCatalog: CustomSectionColumns | undefined;
  /** `meta.sortable` ostatniej odpowiedzi (`undefined` = jeszcze nie przyszła). */
  sortableSlugs: readonly string[] | undefined;
  /** `meta.filterable` ostatniej odpowiedzi (`undefined` = jeszcze nie przyszła). */
  filterableSlugs: readonly string[] | undefined;
  /**
   * Wczytane opcje filtra kartoteki własnej (użytkownicy, relacje, słowniki); puste dla
   * pozostałych zestawów.
   */
  moduleFilterOptions: ModuleListFilterOptions;
}

/** Zbudowane kolumny listy razem z parametrem `?columns=` zapytania. */
interface ListColumns {
  columns: ColumnDefinition<DecisionSectionRow>[];
  /**
   * `?columns=` — komplet kolumn zestawu (także ukrytych domyślnie); `undefined` = zestaw stały,
   * którego zapytanie nie niesie `columns`.
   */
  columnsParam: string | undefined;
  /**
   * Id kolumn z sumą „Razem", gdy zestaw zna je dopiero po zbudowaniu kolumn (kartoteka własna:
   * pola „Kwota (PLN)" z katalogu odpowiedzi); brak = `SectionListSource.sumSlugs`.
   */
  sumSlugs?: readonly string[];
  /** Format sumy w komórce „Razem" (kartoteka własna: jak komórki pola); brak = kwota z walutą. */
  formatSum?: (columnId: string, sum: CurrencySum) => ReactNode;
}

/**
 * Wszystko, co lista sekcji bierze od zestawu — niezależnie od tego, czy kolumny pochodzą z katalogu
 * pól (`DecisionSectionKit`), czy są stałe (`StaticSectionKit`). Referencja musi być stabilna
 * (`useMemo` w opakowaniu): od niej zależą kolumny.
 */
interface SectionListSource {
  targetModuleCode: string;
  sortSlugOverrides: Readonly<Record<string, string>>;
  /** Id kolumn kwot z sumą „Razem" (puste = sekcja bez sum). */
  sumSlugs: readonly string[];
  /** Czy zakładka „Wartości" filtra pyta o podpowiedzi (`section/distinct`) — tylko zestawy z katalogiem pól. */
  usesDistinctValues: boolean;
  /**
   * Czy sekcja ma filtr „Firma" (kolumnę „Firma" z listą firm rekordów z serwera,
   * `companyOptions=1`). Lista kartoteki własnej nie ma kolumny „Firma" — jej sekcja też nie.
   */
  hasCompanyFilter: boolean;
  /**
   * Czy katalog kolumn (pola, układ, kategorie) przychodzi w odpowiedzi sekcji (kartoteka własna):
   * tabela montuje kolumny dopiero po pierwszej odpowiedzi, a sumy „Razem" są proszone od razu
   * (kolumn kwot nie znamy z góry).
   */
  catalogFromResponse: boolean;
  buildColumns: (ctx: ListColumnsContext) => ListColumns;
}

/**
 * Katalog kartoteki własnej i opcje jej filtrów — trzyma je opakowanie sekcji kartoteki własnej
 * (`CustomSectionTable`), bo tylko ona potrzebuje haka wczytującego opcje (użytkownicy, relacje,
 * słowniki); pozostałe zestawy niczego tu nie dostają i nie wołają go wcale.
 */
interface CustomCatalogState {
  /** Katalog z ostatniej odpowiedzi sekcji; `undefined` = jeszcze nie przyszedł. */
  catalog: CustomSectionColumns | undefined;
  /** Zgłoszenie katalogu z odpowiedzi (z zachowaniem tożsamości, gdy treść bez zmian). */
  onCatalog: (catalog: CustomSectionColumns) => void;
  /** Wczytane opcje filtra wiersza filtrów (patrz `useModuleListFilterOptions`). */
  filterOptions: ModuleListFilterOptions;
  /**
   * Identyfikator podpowiedzi filtra sekcji (`buildSectionDistinctTableId`) — liczony raz w
   * opakowaniu (potrzebuje go hak opcji filtra) i używany też przez tabelę.
   */
  distinctTableId: string;
}

interface DecisionSectionListProps extends Omit<DecisionSectionTableProps, 'kit'> {
  source: SectionListSource;
  listConfig: TableLayoutConfig;
  /** Tylko zestaw z katalogiem w odpowiedzi (`source.catalogFromResponse`). */
  customCatalog?: CustomCatalogState;
}

/**
 * Odpowiedź sekcji z adnotacjami, o co poproszono: o sumy (`requestedSums`) — po niej panel
 * rozpoznaje dane wczytane w widoku „Karty" (bez sum), gdy użytkownik wraca do „Listy" — i o firmy
 * do filtra „Firma" (`requestedCompanyOptions`) — po niej rozpoznaje odpowiedź z pamięci
 * podręcznej, która listy firm nie niosła (patrz opis pliku).
 */
type SectionQueryData = DecisionSectionResponse<DecisionSectionRow> & {
  requestedSums: boolean;
  requestedCompanyOptions: boolean;
};

/**
 * Odmowa serwera, której ani ponowienie, ani odpytywanie nie zmieni (stan trwa do zmiany klucza
 * zapytania): brak uprawnienia (403) albo moduł kartoteki wyłączony (402).
 */
type SectionRefusal = 'forbidden' | 'moduleInactive';

function sectionRefusalOf(error: unknown): SectionRefusal | null {
  if (isForbiddenError(error)) return 'forbidden';
  if (isModuleInactiveSectionError(error)) return 'moduleInactive';
  return null;
}

/**
 * Błąd, którego ani ponowienie, ani odpytywanie nie zmieni: odmowa (403 / 402, `sectionRefusalOf`)
 * albo 400 `UNKNOWN_SECTION` (klucz sekcji, którego serwer nie zna — każde kolejne zapytanie dostanie
 * tę samą odpowiedź). Wspólna reguła dla ponowień (`retrySectionQuery`) i auto-odświeżania.
 */
function isTerminalSectionError(error: unknown): boolean {
  return sectionRefusalOf(error) !== null || isUnknownSectionError(error);
}

/**
 * Reguła `retry` zapytania sekcji: jak `retryUnlessForbidden`, ale też bez ponowień po 402 oraz po
 * 400 `UNKNOWN_SECTION` (patrz `isTerminalSectionError`).
 */
function retrySectionQuery(failureCount: number, error: unknown): boolean {
  return !isTerminalSectionError(error) && retryUnlessForbidden(failureCount, error);
}

/**
 * Komórka wiersza „Razem": suma kolumny w każdej walucie osobno, jedna pod drugą. Wołający daje ją
 * tylko kolumnie, która ma jakąkolwiek kwotę (kolumna bez żadnej — `[]` — zostaje pusta).
 * Wyrównanie dziedziczy po komórce stopki (`align` kolumny).
 */
function SectionSumCell({
  groupKey,
  columnId,
  sums,
  formatSum,
}: {
  groupKey: string;
  columnId: string;
  sums: DecisionSectionSums[string];
  /** Format sumy zestawu (`ListColumns.formatSum`); brak = kwota z kodem waluty. */
  formatSum: ListColumns['formatSum'];
}): ReactElement {
  return (
    <div data-testid={`approvals-section-sum-${groupKey}-${columnId}`}>
      {sums.map((sum) => (
        <div
          key={sum.currencyCode}
          className="tabular-nums"
          data-testid={`approvals-section-sum-${groupKey}-${columnId}-${sum.currencyCode}`}
        >
          {formatSum === undefined
            ? formatMoney(sum.total, sum.currencyCode)
            : formatSum(columnId, sum)}
        </div>
      ))}
    </div>
  );
}

/**
 * Tabela z danymi — montowana po rozstrzygnięciu zestawu kolumn: katalog pól i układ listy albo zestaw
 * stały; kartoteka własna montuje się od razu, bez kolumn, które dochodzą z pierwszą odpowiedzią sekcji.
 */
function DecisionSectionList({
  groupKey,
  source,
  overdue,
  scope,
  refetchInterval,
  onOpenDecision,
  viewMode,
  selection,
  listConfig,
  customCatalog,
}: DecisionSectionListProps) {
  const { t } = useTranslation();
  const navigateToTarget = useNavigateToTarget();
  // Zakres „Zespół" = tylko podgląd (jak zwykła lista Zespołu): bez przycisków decyzji i zaznaczania.
  const readOnly = scope === 'team';

  // Sort serwerowy + paginacja (silnik list). `defaultSort` układu = początkowy sort zapytania
  // (komponent montuje się dopiero po rozstrzygnięciu układu, więc wartość początkowa jest już znana).
  // Wyjątek: kartoteka własna montuje się przed katalogiem (układ jest wtedy pusty), więc nie ma sortu
  // początkowego — jak na liście kartoteki, bez kliknięcia w nagłówek serwer sortuje po pozycji i
  // dacie utworzenia.
  const {
    isCoreColumnSortable,
    isCoreColumnFilterable,
    resetPage,
    queryParams,
    tableProps,
    pageSize,
  } = useServerListControls({
    defaultPageSize: SECTION_PAGE_LIMIT,
    defaultSortColumnId: listConfig.defaultSort?.column,
    defaultSortDirection: listConfig.defaultSort?.direction,
    columnIdToSortSlug: source.sortSlugOverrides,
  });
  const { onPageChange, onSort } = tableProps;

  // Zmiana zakresu (kafelek „Przeterminowane") zmienia zbiór wierszy — wracamy na pierwszą stronę
  // (wzorzec „dopasuj stan do zmienionej wartości w trakcie renderu", bez klatki ze starą stroną).
  const [prevOverdue, setPrevOverdue] = useState(overdue);
  if (prevOverdue !== overdue) {
    setPrevOverdue(overdue);
    resetPage();
  }

  // `meta` ostatniej odpowiedzi — REALNY stan: kolumny przeliczają się, gdy metadane dotrą.
  const [sortableSlugs, setSortableSlugs] = useState<string[] | undefined>(undefined);
  const [filterableSlugs, setFilterableSlugs] = useState<string[] | undefined>(undefined);
  // Katalog kartoteki własnej z ostatniej odpowiedzi (`columns`) — REALNY stan opakowania
  // (`CustomSectionTable`), które woła też hak opcji filtra; uzupełniany efektem razem z `meta`.
  // Do pierwszej odpowiedzi tabela nie ma kolumn. Zestawy z katalogiem pól go nie mają.
  const responseCatalog = customCatalog?.catalog;
  const onResponseCatalog = customCatalog?.onCatalog;

  // Liczba błędów zapytania bieżącego klucza w chwili, gdy ten klucz stał się bieżący (patrz `loadFailed`).
  const [errorBaseline, setErrorBaseline] = useState<{ keyId: string; errors: number } | null>(
    null,
  );

  const [headerFilters, setHeaderFilters] = useState<ColumnHeaderFilters>({});
  const [columnFilters, setColumnFilters] = useState<ColumnFilters>({});
  const debouncedColumnFilters = useDebouncedValue(columnFilters, COLUMN_FILTER_DEBOUNCE_MS);

  // `getRefLabels` — getter stabilny (patrz `useExtRefLabelsGetter`), aktualizowany niżej PO zapytaniu.
  const [getRefLabels, setRefLabels] = useExtRefLabelsGetter();
  // Odwołanie, którego serwer nie rozwiązał (brak klucza w `extRefLabels`), nie pokazuje surowego id.
  const unresolvedRefLabel = t('tenant.processes.approvals.sections.noAccess');

  // Filtr „Firma": firmy REKORDÓW sekcji z serwera, pamiętane osobno dla każdego zakresu (faza 1
  // haka — przed budową kolumn; faza 2 stoi PO zapytaniu, niżej). Szczegóły w haku.
  // Firma z górnego paska — sekcja zawęża się jak lista decyzji (`MyPendingDecisionsTab`);
  // wchodzi też do pamięci opcji filtra „Firma", żeby po zmianie firmy nie pokazać starej listy.
  const activeCompanyId = companyIdParam(useActiveCompanyScope());
  const companyOptions = useSectionCompanyOptions({
    scope,
    overdue,
    companyId: activeCompanyId,
    companyFilter: headerFilters.company,
    sortColumn: tableProps.sortColumn,
    sortDirection: tableProps.sortDirection,
  });
  const { companies } = companyOptions;

  // Opcje list wyboru w wierszu filtrów kartoteki własnej (użytkownicy, relacje z rekordów SAMEJ
  // sekcji, słowniki) — wczytuje je opakowanie `CustomSectionTable` (potrzebne PRZED budową
  // kolumn, a znane dopiero po katalogu z odpowiedzi); pozostałe zestawy mają puste.
  const sectionDistinctTableId =
    customCatalog?.distinctTableId ?? buildSectionDistinctTableId(groupKey, overdue, scope);
  const moduleFilterOptions = customCatalog?.filterOptions ?? EMPTY_MODULE_LIST_FILTER_OPTIONS;

  const built = useMemo(
    () =>
      source.buildColumns({
        t,
        getRefLabels,
        companies,
        isSortable: (slug) => isCoreColumnSortable(slug, sortableSlugs),
        isFilterable: (slug) => isCoreColumnFilterable(slug, filterableSlugs),
        unresolvedRefLabel,
        responseCatalog,
        sortableSlugs,
        filterableSlugs,
        moduleFilterOptions,
      }),
    [
      source,
      t,
      getRefLabels,
      companies,
      isCoreColumnSortable,
      sortableSlugs,
      isCoreColumnFilterable,
      filterableSlugs,
      unresolvedRefLabel,
      responseCatalog,
      moduleFilterOptions,
    ],
  );
  const { columnsParam } = built;

  // Układ listy zestawu: z katalogu odpowiedzi (kartoteka własna) albo wczytany przed montażem
  // tabeli. Obiekt zmienia tożsamość tylko wraz ze ZMIANĄ TREŚCI układu: katalog z nowej
  // odpowiedzi jest stabilizowany (`stabilizeCustomSectionCatalog` w opakowaniu sekcji), więc
  // ani nowy klucz zapytania (sort, filtr, strona), ani przebudowa kolumn nie dają nowego obiektu
  // — inaczej tabela zerowałaby konfigurację kolumn (ukryte kolumny, szerokości).
  const catalogLayoutConfig = responseCatalog?.layout?.listConfig;
  const effectiveListConfig = useMemo(
    () => (source.catalogFromResponse ? customSectionListConfig(catalogLayoutConfig) : listConfig),
    [source.catalogFromResponse, catalogLayoutConfig, listConfig],
  );

  // Kolumny kwot z sumą „Razem", które ta sekcja REALNIE ma: zestaw wymienia je z góry (albo, dla
  // kartoteki własnej, po zbudowaniu kolumn), a lista kolumn zależy od katalogu pól tenanta. Bez
  // żadnej takiej kolumny nie prosimy o sumy i nie rysujemy stopki.
  const sumSlugs = built.sumSlugs ?? source.sumSlugs;
  const presentSumSlugs = useMemo(
    () => sumSlugs.filter((slug) => built.columns.some((column) => column.id === slug)),
    [sumSlugs, built.columns],
  );
  // Sumy „Razem" i prośba o nie: tylko widok „Lista" i tylko sekcja z kolumnami kwot. Kartoteka
  // własna nie zna kolumn kwot przed pierwszą odpowiedzią, więc do jej przyjścia prosi o sumy z
  // góry (bez pól kwot serwer odpowiada `{}`) — inaczej sumy wymagałyby drugiego zapytania.
  const awaitingCatalog = source.catalogFromResponse && responseCatalog === undefined;
  const wantSums = viewMode === 'list' && (awaitingCatalog || presentSumSlugs.length > 0);

  // Wspólna droga „filtry kolumn → `headerFilters` żądania" ze stroną listy kartoteki własnej
  // (`buildModuleListHeaderFilters`): opcja „(Puste)" listy użytkownika i relacji idzie do serwera
  // jako `null` w `selectedValues`, rozstrzygana po typie pola z CAŁEGO katalogu sekcji. Zestawy bez
  // katalogu w odpowiedzi (kartoteki z katalogu pól, Handel) nie mają takiej opcji — brak pól =
  // filtry bez zmian.
  const catalogFields = responseCatalog?.fields ?? NO_MODULE_FIELDS;
  const serializedHeaderFilters = useMemo(() => {
    const merged = buildModuleListHeaderFilters({
      columns: built.columns,
      fields: catalogFields,
      headerFilters,
      columnFilters: debouncedColumnFilters,
    });
    return merged.length > 0 ? JSON.stringify(merged) : undefined;
  }, [headerFilters, debouncedColumnFilters, built.columns, catalogFields]);

  // Prefiks `['processes', …]` — decyzja (okno „Akceptacja") unieważnia całe `['processes']`,
  // więc sekcja odświeża się razem z resztą ekranu. Klucz NIE niesie prośby o sumy (`wantSums`):
  // przełączenie „Lista" ↔ „Karty" nie może zmieniać klucza (wczytywałoby wiersze od nowa na danych
  // zastępczych i zdejmowało zaznaczenie sekcji) — patrz `requestedSums` niżej.
  const sectionQueryKey = [
    'processes',
    'my-pending-decisions',
    'section',
    {
      key: groupKey,
      overdue,
      scope,
      companyId: activeCompanyId ?? null,
      headerFilters: serializedHeaderFilters ?? null,
      columns: columnsParam,
      ...queryParams,
    },
  ];
  const sectionKeyId = JSON.stringify(sectionQueryKey);

  // Czy OSTATNI pokazany stan sekcji był stanem błędu (aktualizowane w efekcie, nie w renderze).
  // Czyta je `placeholderData` niżej: po błędzie nowy klucz (dopisany znak filtra, klik sortu) NIE
  // dostaje danych zastępczych sprzed błędu — to wiersze spoza nowego filtra, z klikalnymi decyzjami.
  const lastShownFailedRef = useRef(false);
  const keepPreviousUnlessFailed = useCallback(
    (previous: SectionQueryData | undefined) => (lastShownFailedRef.current ? undefined : previous),
    [],
  );

  // Lista firm do filtra „Firma" (`companyOptions=1`) tylko w zapytaniu „bazowym" albo dopóki nie
  // znamy listy dla bieżącego zakresu (reguła → `wantCompanyOptionsFor`). Flaga NIE wchodzi do
  // klucza zapytania (jak `wantSums`): odpowiedź bez listy nie zeruje znanej listy.
  const wantCompanyOptions =
    source.hasCompanyFilter &&
    companyOptions.wantCompanyOptionsFor(queryParams.offset, serializedHeaderFilters);

  const sectionQ = useQuery({
    queryKey: sectionQueryKey,
    // Sumy („Razem") tylko w widoku „Lista" i tylko dla sekcji z kolumnami kwot; firmy do filtra „Firma"
    // tylko wg `wantCompanyOptions` (serwer liczy je bez filtrów nagłówka, więc lista nie zawęża się
    // przy filtrowaniu). `requestedSums` / `requestedCompanyOptions` zapisują, o co poproszono —
    // odpowiedź z „Kart" nie ma sum, a odpowiedź z pamięci podręcznej może nie mieć listy firm.
    queryFn: async (): Promise<SectionQueryData> => ({
      ...(await fetchDecisionSection<DecisionSectionRow>({
        key: groupKey,
        overdue,
        scope: readOnly ? 'team' : undefined,
        companyId: activeCompanyId,
        limit: queryParams.limit,
        offset: queryParams.offset,
        sort: queryParams.sort,
        order: queryParams.order,
        headerFilters: serializedHeaderFilters,
        columns: columnsParam,
        sums: wantSums,
        companyOptions: wantCompanyOptions,
      })),
      requestedSums: wantSums,
      requestedCompanyOptions: wantCompanyOptions,
    }),
    // Trzyma poprzednie dane przy zmianie strony/sortu/filtra — bez migania ładowaniem (z wyjątkiem
    // klucza następującego po błędzie, patrz `lastShownFailedRef`).
    placeholderData: keepPreviousUnlessFailed,
    // Po 403 (brak uprawnienia podglądu zespołu) i po 402 (moduł kartoteki wyłączony po otwarciu
    // sekcji) bez ponawiania i bez dalszego odpytywania — to się nie zmieni samo (jak w zapytaniach
    // o nagłówki koszyków); odświeżanie wraca po zmianie klucza zapytania. Bez ponawiania i bez
    // odpytywania także po 400 `UNKNOWN_SECTION` (serwer nie zna klucza sekcji — każde kolejne
    // zapytanie dostanie to samo; `isTerminalSectionError`).
    retry: retrySectionQuery,
    refetchInterval: (query) =>
      isTerminalSectionError(query.state.error) ? false : refetchInterval,
  });
  // Werdykt odmowy (403 / 402) dla bieżącego klucza zapytania: błąd zapytania BEZ danych jest
  // zerowany przy każdym kolejnym pobraniu (odświeżenie, unieważnienie), a stan błędu
  // (`loadFailed`) wtedy trwa — bez zapamiętanego werdyktu łagodny komunikat ustępowałby na czas
  // pobrania zwykłemu stanowi błędu. Obecny błąd rozstrzyga (odmowa → zapamiętujemy ją dla klucza,
  // inny błąd → zapominamy); brak błędu (pobranie w toku) zostawia ostatni werdykt tego klucza.
  // Dopasowanie w trakcie renderu jak `errorBaseline` niżej.
  const [refusalVerdict, setRefusalVerdict] = useState<{
    keyId: string;
    refusal: SectionRefusal;
  } | null>(null);
  const rememberedRefusal = refusalVerdict?.keyId === sectionKeyId ? refusalVerdict.refusal : null;
  if (sectionQ.error !== null) {
    const refusalNow = sectionRefusalOf(sectionQ.error);
    if (refusalNow !== rememberedRefusal) {
      setRefusalVerdict(refusalNow === null ? null : { keyId: sectionKeyId, refusal: refusalNow });
    }
  }
  setRefLabels(sectionQ.data?.extRefLabels);
  // Czy sekcja ma zaznaczone wiersze (któryś z jej wczytanych kroków jest zaznaczony). Zaznaczenie
  // wstrzymuje auto-odświeżanie zakładki, więc nie wolno go omijać dociąganiem sum ani listy firm
  // (oba ponowienia niżej) — liczone tu raz, bo potrzebują tego oba haki.
  const markedStepIds = selection?.markedStepIds;
  const hasMarkedRows =
    !readOnly &&
    markedStepIds !== undefined &&
    (sectionQ.data?.items ?? []).some((row) =>
      row.decisions.some((decision) => markedStepIds.has(decision.stepInstanceId)),
    );
  // Kolejność: ponowienie po sumy PRZED ponowieniem po listę firm — taka, jak w kodzie sprzed
  // wydzielenia hooków. Gdy w jednym zatwierdzeniu potrzebne są oba, oba wołają `refetch()`,
  // a drugie anuluje pierwsze (`cancelRefetch: true` domyślnie); poza tym kolejność nie jest
  // krytyczna.
  useSectionSumsRefetch({ query: sectionQ, wantSums, hasMarkedRows });
  useSectionCompanyOptionsFromQuery({
    options: companyOptions,
    query: sectionQ,
    hasMarkedRows,
    enabled: source.hasCompanyFilter,
  });
  // `meta` do stanu przez EFEKT, nie w renderze (to `useState`) — i TYLKO gdy są dane: po błędzie
  // zapytania z nowym kluczem `data` jest puste, a wyzerowanie slugów odebrałoby zwykłym kolumnom
  // rdzennym kontrolkę filtra (`headerFilter`) i wyłączyło sort nagłówków. Filtr wpisany przed błędem
  // zostawałby przy tym w zapytaniu, ale bez widocznej kontrolki, więc użytkownik nie miałby jak go
  // zmienić. Trzymamy ostatnie znane metadane — z zachowaniem tożsamości list o tej samej treści:
  // każdy nowy klucz zapytania (sort, filtr, strona) niesie świeże kopie `meta`, a nowa lista
  // przebudowywałaby kolumny i zerowała konfigurację kolumn tabeli (ukryte kolumny, szerokości).
  const metaSortable = sectionQ.data?.meta.sortable;
  const metaFilterable = sectionQ.data?.meta.filterable;
  useEffect(() => {
    if (metaSortable !== undefined) {
      setSortableSlugs((previous) => keepEqualSlugList(previous, metaSortable));
    }
  }, [metaSortable]);
  useEffect(() => {
    if (metaFilterable !== undefined) {
      setFilterableSlugs((previous) => keepEqualSlugList(previous, metaFilterable));
    }
  }, [metaFilterable]);
  // Katalog kartoteki własnej (`columns` odpowiedzi) — jak `meta`: tylko gdy są dane, żeby błąd
  // zapytania z nowym kluczem nie odbierał tabeli kolumn. Zestawy z katalogiem pól go nie niosą.
  // Katalog bez list pól i kategorii (złamany kontrakt, np. `columns: null`) nie jest przyjmowany —
  // sekcja pokazuje wtedy jawny błąd (patrz `catalogMissing` niżej), a nie wyjątek w budowie kolumn.
  const rawResponseColumns = sectionQ.data?.columns;
  const responseColumns = isCustomSectionCatalog(rawResponseColumns)
    ? rawResponseColumns
    : undefined;
  useEffect(() => {
    if (source.catalogFromResponse && responseColumns !== undefined) {
      onResponseCatalog?.(responseColumns);
    }
  }, [source.catalogFromResponse, responseColumns, onResponseCatalog]);

  // Strona poza zakresem po odświeżeniu (np. 51 rekordów, użytkownik na stronie 2, decyzje zeszły
  // rekordy → `total` 50, `offset` 50): bez korekty zostałaby pusta tabela bez paginacji (ta znika
  // przy `total <= limit`). Przechodzimy na ostatnią istniejącą stronę — w trakcie renderu (wzorzec
  // „dopasuj stan do zmienionej wartości", jak reset przy zmianie zakresu wyżej; warunek sam się
  // wygasza po zmianie strony). Pomijamy dane zastępcze (`keepPreviousData` z poprzedniego
  // zapytania) — ich `total` nie dotyczy bieżącego klucza.
  const reportedTotal = sectionQ.data?.total;
  if (
    reportedTotal !== undefined &&
    !sectionQ.isPlaceholderData &&
    queryParams.offset > 0 &&
    queryParams.offset >= reportedTotal
  ) {
    onPageChange(Math.max(0, Math.ceil(reportedTotal / pageSize) - 1));
  }

  // Błąd stwierdzony dla BIEŻĄCEGO klucza zapytania (po zmianie filtra/sortu/strony) = brak własnych
  // danych tego klucza (nie ma ich wcale albo to dane zastępcze z innego klucza) i błąd tego zapytania
  // od chwili, gdy klucz stał się bieżący. NIE wystarcza `isError`: ponowne pobranie zapytania bez
  // danych (auto-odświeżanie, „Spróbuj ponownie") wraca do `pending`, zeruje `error`, a dane zastępcze
  // podstawiałyby wiersze poprzedniego, udanego klucza — czyli spoza filtra, z klikalnymi decyzjami.
  // `errorUpdateCount` przeżywa to ponowienie (liczy błędy zapytania, nie jest zerowany przy pobraniu),
  // więc stan błędu trwa do udanej odpowiedzi tego klucza. NIE wystarcza też `errorUpdateCount > 0`:
  // wpis w pamięci podręcznej żyje do 5 min (domyślne `gcTime`), więc powrót do klucza, który kiedyś
  // się nie wczytał, pokazałby stary błąd zamiast „Ładowania…". Dlatego liczymy błędy od stanu z chwili
  // zmiany klucza (`errorBaseline`, dopasowywany w trakcie renderu jak `prevOverdue` wyżej).
  // Nieudane odświeżenie w tle przy wczytanych wierszach TEGO klucza to nie ten przypadek —
  // zostają ostatnie wiersze, bez komunikatu.
  const baselineKnown = errorBaseline?.keyId === sectionKeyId;
  if (!baselineKnown) {
    setErrorBaseline({ keyId: sectionKeyId, errors: sectionQ.errorUpdateCount });
  }
  const baselineErrors = baselineKnown ? errorBaseline.errors : sectionQ.errorUpdateCount;
  const loadFailed =
    sectionQ.errorUpdateCount > baselineErrors &&
    (sectionQ.data === undefined || sectionQ.isPlaceholderData);
  useEffect(() => {
    lastShownFailedRef.current = loadFailed;
  }, [loadFailed]);
  // Przy błędzie brak wierszy i `total = 0` (bez paginacji ze starym `total`).
  const rows = useMemo(
    () => (loadFailed ? [] : (sectionQ.data?.items ?? [])),
    [loadFailed, sectionQ.data],
  );
  const total = loadFailed ? 0 : (sectionQ.data?.total ?? 0);
  const tablePrefix = `approvals-section-table-${groupKey}`;
  // Ta sama definicja co „Wyczyść filtry" w tabeli: opis błędu zależy od aktywnych filtrów sekcji.
  const hasActiveFilters =
    Object.keys(headerFilters).length > 0 || Object.keys(columnFilters).length > 0;

  // Zgłaszanie wczytanych kroków zakładce i zdejmowanie zaznaczenia sekcji (zmiana danych, klucza
  // zapytania, zwinięcie) — zbiór efektów z zachowaną kolejnością w `useSectionSelectionSync`.
  // Zwraca też uchwyty potrzebne niżej, dlatego uchwyty zmian strony / sortu / filtra stoją PO nim.
  // Wiersze NIE są pokazane także wtedy, gdy kartoteka własna nie ma jeszcze (poprawnego) katalogu
  // kolumn: zamiast tabeli stoi wtedy „Ładowanie" albo błąd sekcji, więc kroki z odpowiedzi nie mogą
  // być zgłaszane zakładce jako pokazane (licznik, akcja masowa, pauza odświeżania).
  const { onSliceChange, clearOwnSelection } = useSectionSelectionSync({
    groupKey,
    sectionKeyId,
    readOnly,
    selection,
    rowsHidden: loadFailed || awaitingCatalog,
    query: sectionQ,
  });
  // Zmiana sortu i strony z ręki użytkownika zdejmuje zaznaczenie sekcji (zbiór wierszy pod nim
  // się zmienia). Automatyczna korekta strony poza zakresem (wyżej) idzie bezpośrednio przez
  // `onPageChange`, więc sama niczego nie zdejmuje — ale zmienia klucz zapytania, a ten zdejmuje
  // zaznaczenie sekcji w efekcie reagującym na zmianę klucza (`useSectionSelectionSync`):
  // korekta strony też kasuje zaznaczenie.
  const handleSort = useCallback(
    (column: string, direction: 'asc' | 'desc') => {
      clearOwnSelection();
      onSort(column, direction);
    },
    [clearOwnSelection, onSort],
  );
  const handlePageChange = useCallback(
    (page: number) => {
      clearOwnSelection();
      onPageChange(page);
    },
    [clearOwnSelection, onPageChange],
  );
  // Zmiana filtra wraca na pierwszą stronę (paginacja OFFSET) i zdejmuje zaznaczenie sekcji.
  // Filtr „Szukaj…" (`columnFilters`) trafia do zapytania dopiero po opóźnieniu — zaznaczenie
  // zrobione w tym oknie zdejmuje efekt reagujący na zmianę klucza zapytania
  // (`useSectionSelectionSync`).
  const handleHeaderFiltersChange = useCallback(
    (next: ColumnHeaderFilters) => {
      clearOwnSelection();
      setHeaderFilters(next);
      resetPage();
    },
    [clearOwnSelection, resetPage],
  );
  const handleColumnFiltersChange = useCallback(
    (next: ColumnFilters) => {
      clearOwnSelection();
      setColumnFilters(next);
      resetPage();
    },
    [clearOwnSelection, resetPage],
  );

  // Zaznaczanie: tylko zakres „Moje", gdy zakładka na to pozwala i na danych własnych tego klucza
  // zapytania (jak na liście: na danych zastępczych checkboxy znikają). Wiersz = rekord, więc do
  // tabeli idą id rekordów, których wszystkie kroki są zaznaczone (patrz `selection.ts`).
  const selectable =
    !readOnly &&
    selection !== undefined &&
    selection.enabled &&
    !sectionQ.isPlaceholderData &&
    !loadFailed;
  const selectedIds = useMemo(
    () => (markedStepIds === undefined ? [] : selectedRecordIds(rows, markedStepIds)),
    [rows, markedStepIds],
  );
  // Zmiana zaznaczenia w tej tabeli podmienia tylko kroki tej sekcji — zaznaczenie pozostałych sekcji
  // i tabeli pozostałych kartotek zostaje (inaczej „zaznacz wszystko" jednej kasowałoby resztę).
  // „Zaznacz wszystko" przy rekordzie bez kroków rozpoznaje `nextSliceStepIds` (patrz tam).
  const handleSelectionChange = (recordIds: string[]) => {
    onSliceChange?.(
      stepIdsOfRows(rows),
      nextSliceStepIds(rows, markedStepIds ?? NO_MARKED_STEPS, recordIds),
    );
  };

  // Moduł kartoteki wyłączony po otwarciu sekcji (402): zwykły stan błędu z „Spróbuj ponownie"
  // nic by nie dał (ponowienie dostanie tę samą odmowę), więc sam komunikat „Moduł tej kartoteki
  // jest nieaktywny." — ten sam co w oknach decyzji. Werdykt jest zapamiętany dla klucza (patrz
  // `refusalVerdict`).
  if (loadFailed && rememberedRefusal === 'moduleInactive') {
    return (
      <div
        className="text-sm text-text-tertiary"
        data-testid={`approvals-section-module-inactive-${groupKey}`}
      >
        {t(TARGET_MODULE_INACTIVE_I18N_KEY)}
      </div>
    );
  }

  // Brak uprawnienia podglądu zespołu (403) — łagodny komunikat jak na zwykłej liście Zespołu, nie
  // surowy błąd. W praktyce kafelek „Zespół" jest wtedy ukryty, więc to zabezpieczenie. Werdykt „403"
  // jest zapamiętany dla klucza (patrz `refusalVerdict`), więc komunikat nie ustępuje na czas
  // ponownego pobrania.
  if (readOnly && loadFailed && rememberedRefusal === 'forbidden') {
    return (
      <div
        className="text-sm text-text-tertiary"
        data-testid={`approvals-section-team-permission-denied-${groupKey}`}
      >
        {t('tenant.processes.approvals.teamPermissionDenied')}
      </div>
    );
  }

  // Kartoteka własna przed pierwszą odpowiedzią nie ma katalogu, więc i kolumn — zamiast tabeli
  // „Ładowanie" albo błąd pierwszego wczytania (z ponowieniem). Odpowiedź bez katalogu mimo
  // klucza kartoteki własnej (brak katalogu, `columns: null` albo katalog bez list pól) to złamany
  // kontrakt serwera: też błąd, nie wieczne „Ładowanie" ani wyjątek. Odmowy 402 i 403 zostały
  // obsłużone wyżej.
  if (awaitingCatalog) {
    const catalogMissing =
      sectionQ.data !== undefined && !isCustomSectionCatalog(sectionQ.data.columns);
    if (loadFailed || catalogMissing) {
      return (
        <SectionLoadError
          groupKey={groupKey}
          onRetry={() => void sectionQ.refetch()}
          retrying={sectionQ.isFetching}
        />
      );
    }
    return <SectionLoading groupKey={groupKey} />;
  }

  // Wiersz „Razem": tylko widok „Lista", sekcja z kolumnami kwot i wczytanymi rekordami. Sumy pochodzą
  // z odpowiedzi serwera dla bieżących filtrów (ze wszystkich stron); odpowiedź bez nich (np. dane z
  // „Kart" przed ponowieniem zapytania) nie rysuje stopki. Przy błędzie wczytania (`total = 0`) jej nie ma.
  // Tabela sama pomija wiersz, gdy żadna WIDOCZNA kolumna nie ma sumy (np. kolumny kwot ukryte).
  const footerSums =
    wantSums && presentSumSlugs.length > 0 && total > 0 ? sectionQ.data?.sums : undefined;

  return (
    <div className="flex flex-col gap-2">
      <ConfigurableTable<DecisionSectionRow>
        // UNIKALNY `tableId` sekcji — NIGDY realny slug kartoteki: zakładka „Layouty" super-usera
        // tworzy/usuwa układy po `tableId`. Układ i tak nie jest zapisywany (`persistConfig={false}`).
        tableId={`processes-decisions:${groupKey}`}
        // Podpowiedzi wartości filtra („Wartości" w popupie nagłówka) liczone z rekordów SAMEJ sekcji
        // (`section/distinct`, z zakresem „Przeterminowane" / „Zespół"), nie z całej kartoteki — jak
        // lista faktur, ale na zbiorze decyzji użytkownika (albo jego podwładnych). Zestaw stały
        // (Handel) ich nie ma (serwer odpowiada 400), a `undefined` NIE wyłącza podpowiedzi samo
        // z siebie: tabela zastępuje go domyślnym `distinctTableId = tableId`
        // (`processes-decisions:…`). O podpowiedzi nie pyta dlatego, że `buildEndpointUrl` nie
        // mapuje tego prefiksu na żaden endpoint, więc zapytanie jest wyłączone (test:
        // `useDistinctValues.test.tsx`).
        distinctTableId={source.usesDistinctValues ? sectionDistinctTableId : undefined}
        testIdPrefix={tablePrefix}
        columns={built.columns}
        data={rows}
        // Ponowienie po błędzie nie zamienia stanu błędu w „Ładowanie…" (trwanie pokazuje przycisk).
        isLoading={sectionQ.isLoading && !loadFailed}
        getRowId={(row) => row.id}
        headerFilters={headerFilters}
        onHeaderFiltersChange={handleHeaderFiltersChange}
        filterable
        columnFilters={columnFilters}
        onColumnFiltersChange={handleColumnFiltersChange}
        sortColumn={tableProps.sortColumn}
        sortDirection={tableProps.sortDirection}
        onSort={handleSort}
        selectable={selectable}
        selectedIds={selectedIds}
        onSelectionChange={handleSelectionChange}
        // Układ listy od administratora, tylko do odczytu (bez fetchu układów i bez zapisu).
        persistConfig={false}
        previewLayoutConfig={effectiveListConfig}
        // Tryb widoku sterowany z zewnątrz (wspólny ze zwykłą listą); BEZ `onViewModeChange`, bo tabela
        // wołałaby go przy montażu z `defaultViewMode` układu administratora i nadpisywałaby wybór.
        // Karty bez `titleColumnId` — tytuł jak w karcie listy kartoteki (pierwsza kolumna).
        viewMode={viewMode}
        allowedViewModes={['list', 'cards']}
        // Wiersz „Razem" pod tabelą: etykieta w pierwszej kolumnie, suma pod każdą kolumną kwot
        // zestawu, którą sekcja ma (komórki innych kolumn i kolumn bez żadnej kwoty zostają puste).
        footerLabel={
          footerSums !== undefined ? t('tenant.processes.approvals.sections.totalLabel') : undefined
        }
        footerCells={
          footerSums !== undefined
            ? (column) => {
                const sums = presentSumSlugs.includes(column.id)
                  ? footerSums[column.id]
                  : undefined;
                return sums === undefined || sums.length === 0 ? null : (
                  <SectionSumCell
                    groupKey={groupKey}
                    columnId={column.id}
                    sums={sums}
                    formatSum={built.formatSum}
                  />
                );
              }
            : undefined
        }
        actionsColumnLabel={
          readOnly ? t('common.actions') : t('tenant.processes.approvals.inline.columnLabel')
        }
        stickyActionsColumn
        renderActions={(row, { placement }) => (
          <div
            className={cn(
              'flex items-center justify-end gap-3',
              placement === 'cards' && 'flex-wrap',
            )}
            data-testid={`approvals-section-row-actions-${row.id}`}
          >
            {/* Zakres „Zespół" (`readOnly`): bez przycisków decyzji — zostaje „Otwórz formularz". */}
            {!readOnly && (
              <div className="flex flex-col items-end gap-1.5">
                {row.decisions.map((decision) => (
                  <div key={decision.stepInstanceId} className="flex items-center gap-2">
                    {/* Przy kilku otwartych krokach rekordu etykieta kroku odróżnia zestawy przycisków. */}
                    {row.decisions.length > 1 && (
                      <span
                        className="max-w-[10rem] truncate text-xs text-text-tertiary"
                        title={decision.currentNodeLabel ?? undefined}
                        data-testid={`approvals-section-step-${decision.stepInstanceId}`}
                      >
                        {decision.currentNodeLabel ??
                          t('tenant.processes.approvals.fallback.missingNode')}
                      </span>
                    )}
                    <DecisionButtons
                      routes={decision.availableRoutes}
                      size="sm"
                      // Układ listy (jedna linia) tylko w TABELI, gdzie kolumna jest przypięta; karta
                      // jest wąska, więc ma zwykły układ (zawijane przyciski) — jak na zwykłej liście.
                      layout={placement === 'table' ? 'list' : undefined}
                      groupLabel={t('tenant.processes.approvals.inline.groupLabel', {
                        number: decision.instanceNumber,
                      })}
                      stepBackAvailable={decision.stepBackAllowed}
                      onOpenStepBack={() => onOpenDecision(decision, { kind: 'stepBack' })}
                      onOpen={(routeKey) => onOpenDecision(decision, { kind: 'route', routeKey })}
                      testIdPrefix={`approvals-section-decision-${decision.stepInstanceId}`}
                    />
                  </div>
                ))}
              </div>
            )}
            <Button
              variant="secondary"
              size="sm"
              className="shrink-0 whitespace-nowrap"
              onClick={() => navigateToTarget(source.targetModuleCode, row.id)}
              // Nazwa dostępna niesie numer(y) spraw rekordu — identyczne przyciski w kolejnych
              // wierszach da się odróżnić (patrz `openFormAriaLabel`).
              aria-label={openFormAriaLabel(t, row.decisions)}
              data-testid={`approvals-section-open-form-${row.id}`}
            >
              {t('tenant.processes.approvals.openForm')}
            </Button>
          </div>
        )}
        onRowClick={(row) => navigateToTarget(source.targetModuleCode, row.id)}
        emptyTitle={t('tenant.processes.approvals.sections.empty')}
        // Przy błędzie pusta lista to skutek błędu, nie „brak decyzji" ani „brak wyników filtra":
        // JEDYNY komunikat o błędzie to stan błędu w tabeli (nagłówki i filtry zostają, więc można
        // zdjąć filtr, który wywołał błąd). Przy aktywnych filtrach tabela dodaje „Wyczyść filtry"
        // obok przycisków poniżej; „Spróbuj ponownie" pokazuje trwanie pobrania (`loading` przy
        // `isFetching`), a na stronie > 1 stoi przed nim „Wróć do pierwszej strony" — przy błędzie
        // paginacja jest ukryta (`total = 0`), więc to jedyna droga z błędnej strony bez zmiany filtra.
        errorState={
          loadFailed
            ? {
                title: t('tenant.processes.approvals.sections.loadErrorTitle'),
                description: hasActiveFilters
                  ? t('tenant.processes.approvals.sections.loadErrorDescriptionFiltered')
                  : t('tenant.processes.approvals.sections.loadErrorDescription'),
                action: (
                  <div className="flex flex-wrap items-center justify-center gap-2">
                    {queryParams.offset > 0 && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handlePageChange(0)}
                        data-testid={`approvals-section-first-page-${groupKey}`}
                      >
                        {t('tenant.processes.approvals.sections.backToFirstPage')}
                      </Button>
                    )}
                    <Button
                      variant="secondary"
                      size="sm"
                      loading={sectionQ.isFetching}
                      onClick={() => void sectionQ.refetch()}
                      data-testid={`approvals-section-retry-${groupKey}`}
                    >
                      {t('common.retry')}
                    </Button>
                  </div>
                ),
              }
            : undefined
        }
      />
      <PaginationControls
        total={total}
        offset={queryParams.offset}
        limit={pageSize}
        onOffsetChange={(next) => handlePageChange(Math.floor(next / pageSize))}
        testId={`approvals-section-pagination-${groupKey}`}
      />
    </div>
  );
}

type CatalogSectionProps = Omit<DecisionSectionTableProps, 'kit'> & { kit: DecisionSectionKit };
type StaticSectionProps = Omit<DecisionSectionTableProps, 'kit'> & { kit: StaticSectionKit };
type CustomSectionProps = Omit<DecisionSectionTableProps, 'kit'> & { kit: CustomSectionKit };

/**
 * Kolumny z katalogu pól wczytanego już do końca: opcje list wyboru (słowniki pól `select`, użytkownicy
 * dla „Opiekuna" — ten sam hook co strony list, więc sekcja ma te same filtry co lista kartoteki), potem
 * tabela. Osobny komponent, bo hak opcji potrzebuje katalogu pól, a ten jest znany dopiero po wczytaniu.
 */
function CatalogSectionReady({
  kit,
  fields,
  listConfig,
  ...rest
}: Omit<CatalogSectionProps, 'kit'> & {
  kit: DecisionSectionKit;
  fields: SystemModuleFieldDef[];
  listConfig: TableLayoutConfig;
}): ReactElement {
  const filterOptions = useListFilterOptions(fields, kit.filterOptionsSkipSlugs);
  const source = useMemo<SectionListSource>(
    () => ({
      targetModuleCode: kit.targetModuleCode,
      sortSlugOverrides: kit.sortSlugOverrides,
      sumSlugs: kit.sumSlugs,
      usesDistinctValues: true,
      hasCompanyFilter: true,
      catalogFromResponse: false,
      buildColumns: (ctx) => {
        const built = kit.buildColumns({
          fields,
          t: ctx.t,
          getRefLabels: ctx.getRefLabels,
          companies: ctx.companies,
          isSortable: ctx.isSortable,
          isFilterable: ctx.isFilterable,
          filterOptions,
          unresolvedRefLabel: ctx.unresolvedRefLabel,
        });
        // `?columns=` = KOMPLET kolumn zestawu (nie tylko widoczne): przełączanie widoczności kolumn nie
        // zmienia zapytania, a kolumna ukryta domyślnie (`defaultVisible: false`), włączona z menu kolumn,
        // ma dane. Funkcja zwraca slugi kolumn widocznych wg `defaultVisible`, więc każdą kolumnę podajemy
        // jako widoczną (bez zapisanego układu) — plus zawsze-dołączane i towarzyszące.
        const columnsParam = computeVisibleColumnParams({
          columns: built.columns.map((column) => ({ id: column.id, defaultVisible: true })),
          coreIdToSlug: built.coreIdToSlug,
          extSlugSet: built.extSlugSet,
          alwaysSlugs: kit.alwaysSlugs,
          companions: kit.companions,
        }).join(',');
        return { columns: built.columns, columnsParam };
      },
    }),
    [kit, fields, filterOptions],
  );
  return <DecisionSectionList {...rest} source={source} listConfig={listConfig} />;
}

/**
 * Sekcja kartoteki z katalogiem pól: czeka na katalog pól i układ listy (oba bez bramki uprawnień),
 * potem montuje tabelę z danymi.
 */
function CatalogSectionTable(props: CatalogSectionProps): ReactElement {
  const { groupKey, kit } = props;
  const fieldsQ = useSystemModuleFields(kit.moduleSlug);
  const layoutQ = useSectionListLayout(kit.moduleSlug);

  // Katalog pól jest TWARDĄ zależnością (bez niego nie ma kolumn) — jawny stan błędu, nie pusta tabela.
  if (fieldsQ.isError) return <FieldCatalogErrorState onRetry={() => void fieldsQ.refetch()} />;
  if (layoutQ.isError) {
    return <SectionLoadError groupKey={groupKey} onRetry={() => void layoutQ.refetch()} />;
  }
  if (fieldsQ.data === undefined || layoutQ.data === undefined) {
    return <SectionLoading groupKey={groupKey} />;
  }
  return <CatalogSectionReady {...props} fields={fieldsQ.data} listConfig={layoutQ.data} />;
}

/**
 * Sekcja dokumentów Symfonii Handel: kolumny są stałe, więc nie ma na co czekać — ani katalog pól, ani
 * układ administratora, ani opcje list wyboru nie są wołane. Początkowy sort bierze z zestawu.
 */
function StaticSectionTable({ kit, ...rest }: StaticSectionProps): ReactElement {
  const source = useMemo<SectionListSource>(
    () => ({
      targetModuleCode: kit.targetModuleCode,
      sortSlugOverrides: kit.sortSlugOverrides,
      sumSlugs: kit.sumSlugs,
      usesDistinctValues: false,
      hasCompanyFilter: true,
      catalogFromResponse: false,
      buildColumns: (ctx) => ({
        columns: kit.buildColumns({
          t: ctx.t,
          companies: ctx.companies,
          isSortable: ctx.isSortable,
          isFilterable: ctx.isFilterable,
        }),
        columnsParam: undefined,
      }),
    }),
    [kit],
  );
  const listConfig = useMemo<TableLayoutConfig>(
    () => ({ ...emptyListConfig, defaultSort: kit.defaultSort }),
    [kit],
  );
  return <DecisionSectionList {...rest} source={source} listConfig={listConfig} />;
}

/**
 * Sekcja kartoteki własnej: katalog pól, układ listy i kategorie przychodzą w odpowiedzi sekcji,
 * więc tabela sama czeka na pierwszą odpowiedź (patrz opis pliku) — tu tylko opis zestawu.
 * Kolumny: wspólny builder listy kartoteki własnej (`buildCustomSectionColumns`); id kolumny =
 * slug pola, więc sort i filtr idą po slugu bez przemapowania. Sortu początkowego brak (jak na
 * liście kartoteki).
 *
 * Tu żyje katalog z odpowiedzi (stan zasilany przez tabelę) i hak opcji list wyboru wiersza
 * filtrów (użytkownicy, relacje z rekordów SAMEJ sekcji, słowniki) — tylko ta sekcja go woła;
 * opcje są potrzebne PRZED budową kolumn, a znane dopiero po katalogu z pierwszej odpowiedzi.
 * Memoizacje zależą od prymitywów (`targetModuleCode`, klucz podpowiedzi), nie od obiektu
 * zestawu: kolumny tabeli przebudowują się tylko wraz z danymi.
 */
function CustomSectionTable({ kit, ...rest }: CustomSectionProps): ReactElement {
  const { targetModuleCode } = kit;
  const { groupKey, overdue, scope } = rest;
  const [catalog, setCatalog] = useState<CustomSectionColumns | undefined>(undefined);
  // Nowa odpowiedź niesie świeżą kopię katalogu — zachowujemy tożsamość, gdy treść bez zmian.
  const handleCatalog = useCallback((next: CustomSectionColumns) => {
    setCatalog((previous) =>
      previous === undefined ? next : stabilizeCustomSectionCatalog(previous, next),
    );
  }, []);
  // Zależność od samych pól (nie od całego katalogu): zmiana tylko kategorii nie przelicza pól.
  const catalogFields = catalog?.fields;
  const customFields = useMemo(
    () => (catalogFields === undefined ? NO_MODULE_FIELDS : customSectionListFields(catalogFields)),
    [catalogFields],
  );
  // Liczony raz: potrzebuje go hak opcji filtra, a dalej (`customCatalog`) także tabela.
  const sectionDistinctTableId = buildSectionDistinctTableId(groupKey, overdue, scope);
  const filterOptions = useModuleListFilterOptions({
    tableId: sectionDistinctTableId,
    fields: customFields,
    relationDistinctUrl: (slug) => buildEndpointUrl(sectionDistinctTableId, slug),
  });
  const customCatalog = useMemo<CustomCatalogState>(
    () => ({
      catalog,
      onCatalog: handleCatalog,
      filterOptions,
      distinctTableId: sectionDistinctTableId,
    }),
    [catalog, handleCatalog, filterOptions, sectionDistinctTableId],
  );

  const source = useMemo<SectionListSource>(
    () => ({
      targetModuleCode,
      sortSlugOverrides: NO_SORT_SLUG_OVERRIDES,
      sumSlugs: NO_SUM_SLUGS,
      usesDistinctValues: true,
      hasCompanyFilter: false,
      catalogFromResponse: true,
      buildColumns: (ctx) => {
        const built = buildCustomSectionColumns({
          t: ctx.t,
          catalog: ctx.responseCatalog,
          sortableSlugs: ctx.sortableSlugs,
          filterableSlugs: ctx.filterableSlugs,
          filterOptions: ctx.moduleFilterOptions,
        });
        return {
          columns: built.columns,
          columnsParam: undefined,
          sumSlugs: built.sumSlugs,
          formatSum: built.formatSum,
        };
      },
    }),
    [targetModuleCode],
  );
  return (
    <DecisionSectionList
      {...rest}
      source={source}
      listConfig={emptyListConfig}
      customCatalog={customCatalog}
    />
  );
}

/**
 * Zawartość rozwiniętej sekcji: zestaw z katalogiem pól czeka na katalog i układ listy, zestaw
 * stały (Handel) montuje tabelę od razu, a kartoteka własna czeka na katalog z pierwszej
 * odpowiedzi sekcji. Całość stoi pod stałym regionem `role="status"` sekcji (`SectionStatusRegion`),
 * w którym zapowiadane jest wczytywanie.
 */
export function DecisionSectionTable(props: DecisionSectionTableProps): ReactElement {
  const { kit, groupKey } = props;
  let table: ReactElement;
  if (kit.source === 'static') table = <StaticSectionTable {...props} kit={kit} />;
  else if (kit.source === 'custom') table = <CustomSectionTable {...props} kit={kit} />;
  else table = <CatalogSectionTable {...props} kit={kit} />;
  return <SectionStatusRegion groupKey={groupKey}>{table}</SectionStatusRegion>;
}
